import {
  ActionIcon,
  Badge,
  Button,
  Card,
  Container,
  Group,
  Image,
  Menu,
  Stack,
  Text,
} from "@mantine/core";
import { IconArrowRight, IconChevronDown, IconLanguage } from "@tabler/icons-react";
import React, { useContext, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import MegaLoader from "../../Components/MegaLoader";
import { AllFatherContext } from "../../App";
import { Endpoints } from "../../Lib/endpoints";
import { applyGoogleTranslateLanguageWhenReady } from "../../Lib/googleTranslate";
import { useLanguage } from "../../Lib/i18n/LanguageContext";
import { NonPaginatedResponse } from "../../Lib/responses";
import { BlogProps } from "../../Lib/types";
import { usePerformRequest } from "../../Lib/usePerformRequest";
import "./styles.scss";

const Blog: React.FC = () => {
  const context = useContext(AllFatherContext);
  const { t, i18n } = useTranslation();
  const { currentLanguage } = useLanguage();
  const { data, response, isLoading, error } = usePerformRequest<NonPaginatedResponse<BlogProps[]>>(
    {
      route: Endpoints.GetBlogs(1, 10).route,
      method: Endpoints.GetBlogs(1, 10).method,
    }
  );

  const navigate = useNavigate();

  // Enforce light mode for blogs page
  useEffect(() => {
    document.documentElement.setAttribute("data-theme", "light");
    localStorage.setItem("app-theme", "light");
  }, []);

  const changeLanguage = (language: string) => {
    localStorage.setItem("preferredLanguage", language);
    localStorage.setItem("i18nextLng", "en");
    applyGoogleTranslateLanguageWhenReady(language);
  };

  const getCurrentLanguage = () => {
    switch (currentLanguage) {
      case "en":
        return { code: "en", name: "English", flag: "🇬🇧" };
      case "fr":
        return { code: "fr", name: "Français", flag: "🇫🇷" };
      case "pt":
        return { code: "pt", name: "Português", flag: "🇵🇹" };
      case "de":
        return { code: "de", name: "Deutsch", flag: "🇩🇪" };
      default:
        return { code: "en", name: "English", flag: "🇬🇧" };
    }
  };

  const languages = [
    { code: "en", name: "English", flag: "🇬🇧" },
    { code: "fr", name: "Français", flag: "🇫🇷" },
    { code: "pt", name: "Português", flag: "🇵🇹" },
    { code: "de", name: "Deutsch", flag: "🇩🇪" },
  ];

  return (
    <div className="blogs-bg">
      {/* Navbar */}
      <nav className="blogs-navbar">
        <Container size="lg" className="blogs-navbar-content">
          <Group justify="space-between" align="center" h={64}>
            <ActionIcon
              variant="subtle"
              size="xl"
              className="blogs-navbar-logo"
              onClick={() => navigate("/")}
              style={{ borderRadius: 16, overflow: "hidden" }}
              aria-label="Home"
            >
              <img
                src={context?.assets?.logos?.legacy}
                alt="Logo"
                style={{ height: 44, width: 44, objectFit: "contain" }}
              />
            </ActionIcon>
            <Group gap="md" className="blogs-navbar-links">
              {/* Language Switcher */}
              <Menu shadow="md" width={180} position="bottom-end">
                <Menu.Target>
                  <Button
                    variant="light"
                    color="violet"
                    radius="xl"
                    size="sm"
                    rightSection={<IconChevronDown size={12} />}
                    className="language-switcher-btn"
                  >
                    <Group gap="xs" align="center">
                      <IconLanguage size={16} />
                      <Text size="sm" fw={600}>
                        {getCurrentLanguage().flag}
                      </Text>
                    </Group>
                  </Button>
                </Menu.Target>
                <Menu.Dropdown className="language-dropdown">
                  {languages.map((lang) => (
                    <Menu.Item
                      key={lang.code}
                      onClick={() => changeLanguage(lang.code)}
                      className={`language-item ${currentLanguage === lang.code ? "active" : ""}`}
                    >
                      <Group gap="sm" align="center">
                        <Text>{lang.flag}</Text>
                        <Text size="sm" fw={currentLanguage === lang.code ? 700 : 500}>
                          {lang.name}
                        </Text>
                      </Group>
                    </Menu.Item>
                  ))}
                </Menu.Dropdown>
              </Menu>

              <Button
                radius="xl"
                size="md"
                className="blogs-navbar-btn blogs-navbar-cta"
                onClick={() => navigate("/sign-up")}
              >
                {t("auth.signUp")}
              </Button>
            </Group>
          </Group>
        </Container>
      </nav>
      <div className="blogs-bg-dots">
        <div className="blogs-bg-dot blogs-bg-dot1" />
        <div className="blogs-bg-dot blogs-bg-dot2" />
        <div className="blogs-bg-dot blogs-bg-dot3" />
        <div className="blogs-bg-dot blogs-bg-dot4" />
        <div className="blogs-bg-dot blogs-bg-dot5" />
      </div>
      <Container size="lg" className="blogs-container">
        <Text className="blogs-title" ta="center" mb={40} mt={20}>
          {t("blog.ourBlog")}
        </Text>
        <Group gap={32} align="stretch" justify="center" wrap="wrap">
          {isLoading ? (
            <MegaLoader />
          ) : (
            <>
              {data?.data?.map((blog) => (
                <Card
                  key={blog.id}
                  className="blog-card"
                  radius="xl"
                  shadow="xl"
                  withBorder
                  style={{
                    maxWidth: 370,
                    minWidth: 320,
                    flexGrow: 1,
                    cursor: "pointer",
                    background: "rgba(15,23,42,0.98)",
                    transition: "box-shadow 0.2s",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                  }}
                  onClick={() => navigate(`/blog/${blog.id}`)}
                >
                  <Image
                    src={blog.thumbnail}
                    alt={blog.title}
                    height={180}
                    radius="lg"
                    className="blog-card-image"
                  />
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      flex: 1,
                    }}
                  >
                    <Stack gap="xs" mt="md" style={{ flex: 1 }}>
                      <Group gap="xs">
                        {blog.tags.map((tag) => (
                          <Badge key={tag} color="violet" variant="light" radius="sm">
                            {tag}
                          </Badge>
                        ))}
                      </Group>
                      <Text size="xl" fw={800} lh={1.2} className="blog-card-title">
                        {blog.title}
                      </Text>
                      <Text size="sm" c="dimmed" mb="xs" lineClamp={2}>
                        {blog.description}
                      </Text>
                      <Text size="xs" c="gray.5" mb="xs">
                        {t("blog.by")} {blog.createdBy} •{" "}
                        {new Date(blog.createdAt).toLocaleDateString()}
                      </Text>
                    </Stack>
                    <Button
                      variant="gradient"
                      gradient={{ from: "violet", to: "blue", deg: 90 }}
                      rightSection={<IconArrowRight size={18} />}
                      mt="md"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/blog/${blog.id}`);
                      }}
                      radius="xl"
                      size="md"
                      style={{ fontWeight: 700, marginTop: "auto" }}
                    >
                      {t("blog.readMore")}
                    </Button>
                  </div>
                </Card>
              ))}
            </>
          )}
        </Group>
      </Container>
    </div>
  );
};

export default Blog;
